import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native';
import { checkIsSubmittedBtnClick, setStandupStatus, setToastNotification, clearSuccessMessage, clearToastNotification, clearTopOpportunities } from "../../../store/slices/standUpSlice";
import { clearToastNotification as clearActionPlanToastNotification } from '../../../store/slices/actionPlanSlice';
import { useToast } from '../../../components/LhToastContainer';
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from '../../../constants/fonts';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient } from '../../../api/apiClient';
import NewsFeedOverlay from '../components/NewsFeedOverlay';
import NewsDetailsOverlay from '../components/NewsDetailsOverlay';
import LHModalPop from '../../../components/LHModalPop';
import { getUnfavouriteModalConfig } from '../constants/unfavouriteModalConfig';
import { useLighthouseExecution } from '../hooks/useLighthouseExecution';
import { useFavorites } from '../hooks/useFavorites';
import { useLighthouseTasks } from '../../../hooks/useLighthouseTasks';
import { useStandUpDashboard } from '../hooks/useStandUpDashBoard';
import { useAddNews } from '../hooks/useAddNews';
import { setTopOpportunities as setTopOpportunitiesRedux } from "../../../store/slices/standUpSlice";
import { resetStandupRedirect, fetchKpiMetrics } from '../../../store/slices/standupRedirectSlice';
import { getSessionLockService } from '../../../api/sessionLockService';
import { StandUpRedirectScreenProps, NewsItem } from '../types';
import { HeaderSection } from '../components/CreateStandUp/HeaderSection';
import { StepperNavigation } from '../components/CreateStandUp/StepperNavigation';
import { TopOpportunitiesSection } from '../components/CreateStandUp/TopOpportunitiesSection';
import { ReviewPerformanceTab } from '../components/CreateStandUp/Steps/ReviewPerformanceTab';
import { KeyActivitiesTab } from '../components/CreateStandUp/Steps/KeyActivitiesTab';
import { NewsTab } from '../components/CreateStandUp/Steps/NewsTab';
import { ReviewProgressTab } from '../components/CreateStandUp/Steps/ReviewProgressTab';
import { PlatformContainer } from '../components/CreateStandUp/PlatformContainer';
import StandUpCreateActionPlanScreen from './StandUpCreateActionPlanScreen';
import TopOpportunityDetails from '../components/TopOpportunityDetails';
import { DLOBreadcrumb } from '../../../modules/dlo/components';
import { DLOBreadcrumbItem } from '../../../modules/dlo/types/dlo.types';
import { StandUpBottomActions } from '../components/StandUpBottomActions';
import logger from '../../../utils/logger';
import StandUpErrorModal from '../components/StandUpErrorModal';
import { useStandUpRedirectHandlers } from '../hooks/useStandUpRedirectHandlers';
import { useSelectedLocation } from '../../../hooks/useSelectedLocation';
import { TopOpportunityItem } from '../components/TopOpportunitiesTable';

const StandUpRedirectScreen: React.FC<StandUpRedirectScreenProps> = ({
  onBackPress,
  onHomePress,
  standupType = '',
  standupDate = '',
  standupSessionId = '',
  standupId = '',
  isEditMode = false,
  isViewMode = false,
  viewStandupData = null,
}) => {
  // Get dispatch for Redux actions
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  // Determine which ID to use: when in edit or view mode, use standup_id; otherwise use standup_session_id
  // const effectiveStandupId = (isEditMode || isViewMode) ? standupId : standupSessionId;

  // Get location and user email from Redux
  const selectedLocation = useSelectedLocation();
  const userEmail = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ''
  );
  
  const standupStatus = useSelector((state: RootState) => state.standUp.standupStatus);
  const toastNotification = useSelector((state: RootState) => state.standUp?.toastNotification);
  const isSubmitted = useSelector((state: RootState) => state.standUp.isSubmittedBtnClicked);
  const { ciDashboardData } = useSelector((state: RootState) => state.homescreen);

  // Store initial location to detect changes
  const [initialLocationId] = useState(selectedLocation?.id);

  // Clear ActionPlan toast notification on mount to prevent cross-module notifications
  useEffect(() => {
    dispatch(clearActionPlanToastNotification());
  }, [dispatch]);

  // Display toast notification when it appears in Redux
  useEffect(() => {
    if (toastNotification) {
      toast.showToast({
        header: toastNotification.header,
        subtext: toastNotification.subtext,
        variant: toastNotification.variant,
        position: toastNotification.position || 'top',
        duration: toastNotification.duration || 6000,
      });

      // Clear the toast from Redux after displaying
      dispatch(clearToastNotification());
    }
  }, [toastNotification, toast, dispatch]);

  // Detect location changes and redirect back to StandUpScreen
  useEffect(() => {
    // Check if location has changed from the initial location
    if (initialLocationId && selectedLocation?.id && initialLocationId !== selectedLocation.id) {
      // Release all session locks before redirecting
      const lockService = getSessionLockService();
      if (lockService) {
        lockService.releaseAllLocks().catch((err) => {
          console.error('[StandUp] Error releasing locks on location change:', err);
        });
      }

      // Reset standup redirect state
      dispatch(resetStandupRedirect());

      // Redirect back to StandUpScreen (which will trigger API reloads via useStandUpDashboard hook)
      if (onBackPress) {
        onBackPress();
      }
    }
  }, [selectedLocation?.id, initialLocationId, dispatch, onBackPress]);

  const getInitialTab = () => {
    if (isViewMode) {
      return 'review-progress'; // View mode starts with review progress
    } else if (isEditMode) {
      return 'review-performance'; // Edit mode starts with review performance
    }
    return 'review-performance'; // Default for new standup
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [isTopOpportunitiesExpanded, setIsTopOpportunitiesExpanded] = useState(false);
  const [showActionPlanScreen, setShowActionPlanScreen] = useState(false);
  const [selectedLighthouseTask, setSelectedLighthouseTask] =
    useState<any>(null);
  const [selectedLighthouseCard, setSelectedLighthouseCard] =
    useState<any>(null);
  const [favoriteSparcCards, setFavoriteSparcCards] = useState<string[]>([]);
  const [favoriteLighthouseCards, setFavoriteLighthouseCards] = useState<string[]>([]);
  const [favoriteOpportunities, setFavoriteOpportunities] = useState<string[]>([]);
  const [favoriteLighthouseTasks, setFavoriteLighthouseTasks] = useState<string[]>([]);
  const [allLighthouseTasks, setAllLighthouseTasks] = useState<any[]>([]);
  const [completedLighthouseTaskIds, setCompletedLighthouseTaskIds] = useState<string[]>([]);
  const [allKpiCards, setAllKpiCards] = useState<any[]>([]); // All KPI cards with isFavorite property
  const [newsTitle, setNewsTitle] = useState('');
  const [isBackToEditInViewMode, setIsBackToEditInViewMode] = useState(false);
  const [newsDescription, setNewsDescription] = useState('');
  const [completedStandupStatus, setCompletedStandupStatus] = useState(viewStandupData?.status || ''); // Store completed standup status
  const [sessionNewsList, setSessionNewsList] = useState<NewsItem[]>([]); // For News step - starts empty
  const [showSaveForLaterModal, setShowSaveForLaterModal] = useState(false);
  const [apiNewsList, setApiNewsList] = useState<NewsItem[]>([]); // For Review Progress - from API
  const [standupNewsList, setStandupNewsList] = useState<NewsItem[]>([]); // News associated with this standup
  const [globalNewsList, setGlobalNewsList] = useState<NewsItem[]>([]); // Global news not yet associated
  const [currentReviewNewsPage, setCurrentReviewNewsPage] = useState(0);
  const [currentNewsPage, setCurrentNewsPage] = useState(0);
  const [newsFeedOverlayVisible, setNewsFeedOverlayVisible] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [selectedActionPlanForView, setSelectedActionPlanForView] = useState<any>(null);
  const [newlyCreatedStandupId, setNewlyCreatedStandupId] = useState<string>(''); // Store ID from submitStandUp response
  const [keyActivitiesRefreshFn, setKeyActivitiesRefreshFn] = useState<(() => void) | null>(null);
  const [togglingNewsId, setTogglingNewsId] = useState<number | null>(null);
  const [newsDetailsOverlayVisible, setNewsDetailsOverlayVisible] = useState(false);
  const [selectedNewsId, setSelectedNewsId] = useState<string>('');
  const [newsTitleError, setNewsTitleError] = useState(false);
  const [newsDescriptionError, setNewsDescriptionError] = useState(false);
  
  // Top Opportunities unfavourite modal state
  const [showTopOpportunityUnfavouriteModal, setShowTopOpportunityUnfavouriteModal] = useState(false);
  const [pendingUnfavouriteOpportunityId, setPendingUnfavouriteOpportunityId] = useState<string | null>(null);
  
  const periodWeekData = useSelector((state: any) => state.appData.periodWeek?.data || []);

  // Get session data from Redux for API submission
  const sessionData = useSelector((state: RootState) => state.standupRedirect.session);

  // Get favorites data from Redux
  const favoritesFromRedux = useSelector((state: RootState) => state.standupRedirect.favorites.data);
  const favoritesLoading = useSelector((state: RootState) => state.standupRedirect.favorites.loading);
  const favoritesError = useSelector((state: RootState) => state.standupRedirect.favorites.error);

  // Get API loading states from Redux
  const lighthouseExecutionLoading = useSelector((state: RootState) => state.standupRedirect.reviewPerformance.lighthouseExecution.loading);
  const kpiMetricsLoading = useSelector((state: RootState) => state.standupRedirect.reviewPerformance.kpiMetrics.loading);
  const lighthouseTasksLoading = useSelector((state: RootState) => state.standupRedirect.keyActivities.loading);
  const newsLoading = useSelector((state: RootState) => state.standupRedirect.news.loading);

  // Get top opportunities from Redux (already fetched in StandUpScreen)
  const topOpportunitiesFromRedux = useSelector((state: RootState) => state.standUp.topOpportunities);

  // Filter and sort top opportunities for StandUpRedirectScreen
  // 1. Filter out completed action plans
  // 2. Sort by status priority: overdue -> declined -> in progress
  const filteredAndSortedTopOpportunities = useMemo(() => {
    if (!topOpportunitiesFromRedux || topOpportunitiesFromRedux.length === 0) {
      return topOpportunitiesFromRedux;
    }

    // Filter out completed action plans and frontline employee involvement for daily_flt or daily-frontline standup type
    const filtered = topOpportunitiesFromRedux.filter((item: any) => {
      const status = item.STATUS?.toLowerCase();
      // Exclude completed items
      if (status === 'completed') {
        return false;
      }
      // Exclude frontline employee involvement for daily_flt or daily-frontline standup type
      if ((standupType === 'daily_flt' || standupType === 'daily-frontline') && item.FRONTLINE_EMPLOYEE_INVOLVEMENT === true) {
        return false;
      }
      return true;
    });

    // Sort by status: overdue -> declined -> in progress
    const sorted = [...filtered].sort((a: any, b: any) => {
      // Get effective status (ASSIGNMENT_STATUS if declined, otherwise STATUS)
      const getEffectiveStatus = (item: any) => {
        if (item.ASSIGNMENT_STATUS?.toLowerCase() === 'declined') {
          return 'declined';
        }
        return item.STATUS?.toLowerCase() || '';
      };
      
      const statusA = getEffectiveStatus(a);
      const statusB = getEffectiveStatus(b);
      
      // Define status priority (lower number = higher priority)
      const statusPriority: { [key: string]: number } = {
        'overdue': 1,
        'declined': 2,
        'in progress': 3,
      };
      
      const priorityA = statusPriority[statusA] || 999;
      const priorityB = statusPriority[statusB] || 999;
      
      return priorityA - priorityB;
    });

    return sorted;
  }, [topOpportunitiesFromRedux]);

  // Get news from Redux (already fetched in StandUpScreen)
  const newsFromRedux = useSelector((state: RootState) => state.standUp.news);

  // Stabilize selectedLocation reference for child components/hooks that depend on object identity.
  // If Redux returns a new object instance with the same id/displayText, keep the previous reference.
  const memoizedSelectedLocation = useMemo(
    () => selectedLocation,
    [selectedLocation?.id, selectedLocation?.displayText],
  );

  // Use custom hooks for data fetching
  const { lighthouseCardsData, loading: isLoadingLighthouse, metricTrendData } = useLighthouseExecution();

  // Use StandUp dashboard hook to get refetch functions
  const { fetchTopOpportunitiesData, fetchStandUpData, fetchNewsData, submitStandUp, updateStandup, transformStandupType, transformStandupTypeForFavourites, handleToggleFavoriteOpportunity, isTogglingFavorite, isLoadingTopOpportunities: isLoadingTopOpportunitiesFromHook } = useStandUpDashboard();

  const standupTypeForFavourites = useMemo(
    () => transformStandupTypeForFavourites(standupType),
    [transformStandupTypeForFavourites, standupType],
  );

  // Fetch favorites once at redirect screen level (shared across tabs)
  const favoritesHook = useFavorites({
    standupId,
    standupSessionId: standupSessionId,
    standupType: standupTypeForFavourites,
    isViewMode,
    selectedLocation: memoizedSelectedLocation || undefined,
    isEditMode,
  });

  // Fetch lighthouse tasks once at redirect screen level
  const lighthouseTasksHook = useLighthouseTasks({
    locationId: selectedLocation?.id || '',
    locationName: selectedLocation?.displayText || '',
    period: periodWeekData?.period || '',
    year: periodWeekData?.year || '',
  });

  // Initialize useAddNews hook with standup context
  const { addNews, toggleNewsFavorite, fetchNewsForStandup, isLoading: isAddingNews } = useAddNews({ 
    selectedLocation,
    standupId: standupId || undefined,
    sessionId: standupSessionId || undefined,
  });

  // Fetch KPI metrics and Lighthouse execution data once at redirect screen level
  useEffect(() => {
    if (standupType) {

      // Dispatch async thunk to fetch KPI metrics
      dispatch(fetchKpiMetrics({
        location: selectedLocation.displayText,
        standupType: standupTypeForFavourites,
        metricsType: 'Daily', // Default to Daily, can be changed in ReviewPerformanceTab
        standupId: standupId || undefined,
      }) as any);

      // Dispatch async thunk to fetch Lighthouse execution data
    }
  }, [standupTypeForFavourites, dispatch, selectedLocation?.displayText, standupId]);

  // Fetch standup-specific news when entering standup context (edit/create mode)
  // This fetches both global news and standup-specific news for this standup
  const refreshStandupNews = async () => {
    if (selectedLocation?.displayText && (standupId || standupSessionId)) {
      const result = await fetchNewsForStandup();
      if (result) {
        // Store standup news and global news separately
        setStandupNewsList(result.standupNews);
        setGlobalNewsList(result.globalNews);
        // Combine for the feed section (standup news first, then global)
        const combinedNews = [...result.standupNews, ...result.globalNews];
        setApiNewsList(combinedNews);
        // Clear session news list once API data is loaded to avoid duplicates
        setSessionNewsList([]);
      }
    }
  };

  useEffect(() => {
    refreshStandupNews();
  }, [selectedLocation?.displayText, standupId, standupSessionId]);

  // Fallback: Sync with Redux news data if no standup-specific fetch occurred
  useEffect(() => {
    // Only use Redux news if we don't have standup context (new standup without session yet)
    if (newsFromRedux && newsFromRedux.length > 0 && !standupId && !standupSessionId) {
      setApiNewsList(newsFromRedux);
      setGlobalNewsList(newsFromRedux);
      setStandupNewsList([]);
      // Clear session news list once API data is loaded to avoid duplicates
      // and ensure proper is_truncated values are shown
      setSessionNewsList([]);
    }
  }, [newsFromRedux, standupId, standupSessionId]);

  // Reset news validation errors when user navigates away from News tab
  useEffect(() => {
    if (activeTab !== 'news') {
      setNewsTitleError(false);
      setNewsDescriptionError(false);
    }
  }, [activeTab]);

  // State for KFA favorites from Redux
  // State for favorites from Redux (KFA, KPI, Lighthouse Metric)
  const [kfaFavorites, setKfaFavorites] = useState<any[]>([]);
  const [kpiFavorites, setKpiFavorites] = useState<any[]>([]);
  const [lighthouseMetricFavorites, setLighthouseMetricFavorites] = useState<any[]>([]);

  // Fetch lighthouse execution data from Redux
  const lighthouseExecutionData = useSelector(
    (state: RootState) => state.standupRedirect.reviewPerformance.lighthouseExecution
  );

  // Fetch KPI metrics data from Redux
  const kpiMetricsData = useSelector(
    (state: RootState) => state.standupRedirect.reviewPerformance.kpiMetrics
  );

  // Get selected KPIs from Redux (managed by SparcKpiListSection)
  const selectedKpiFromRedux = useSelector(
    (state: RootState) => state.standupRedirect.reviewProgress.selectedKpi
  );
  // Update favorites when Redux data changes
  useEffect(() => {
    // favoritesFromRedux is already the .data array from selector
    const allFavs = Array.isArray(favoritesFromRedux) ? favoritesFromRedux : [];

    // Normalize and filter by item_type with several common variants
    const isType = (item: any, types: string[]) => {
      if (!item || !item.item_type) return false;
      const t = String(item.item_type).toLowerCase();
      return types.some(type => t === type.toLowerCase());
    };

    const rawKfas = allFavs.filter((item: any) => isType(item, ['kfa', 'KFA']));
    const rawKpis = allFavs.filter((item: any) => isType(item, ['kpi', 'KPI']));
    const rawLighthouseMetrics = allFavs.filter((item: any) => isType(item, ['lighthouse_metric', 'lighthouse-metric', 'lighthousemetric', 'lighthouse_metric']));

    // Transform favorites: map item_name -> description and item.is_favourite -> isfavourite
    const filteredKfas = rawKfas.map((item: any) => {
      const matchingKfaData = ciDashboardData?.data?.find((data: any) => String(data.CI_ID) === String(item.item_id));
      return {
        ...item,
        description: item.item_name,
        isFavorite: item.is_favourite,
        subHeader:matchingKfaData?.['Target End Date'] || '',
        title: item?.module_name?.split('_')[1] || '', // Extract title from module_name if possible
        status: matchingKfaData?.Status || '' // Add matching CI Dashboard data as Status
      };
    });

    // Filter KPI metrics data where title matches favorited item_name
    const filteredKpis: React.SetStateAction<any[]> = [];
    if (kpiMetricsData?.data && kpiMetricsData.data.length > 0) {
      // Get favorited KPI item_names and convert to strings for comparison
      console.log('Raw KPI favorites from Redux:', rawKpis);
      console.log('KPI Metrics data from Redux:', kpiMetricsData.data);
      const favoritedKpiNames = rawKpis.map((item: any) => String(item.item_name));
      // Filter kpiMetricsData based on favorited item_names matching title
      kpiMetricsData.data
        .filter((kpiMetric: any) => favoritedKpiNames.includes(String(kpiMetric.title)))
        .forEach((kpiMetric: any) => {
          // Find the corresponding rawKpi to get isFavorite status and other metadata
          const matchingRawItem = rawKpis.find((item: any) => String(item.item_name) === String(kpiMetric.title));
          filteredKpis.push({
            ...kpiMetric,
            description: kpiMetric.title,
            isFavorite: matchingRawItem?.is_favourite || false,
            item_id: matchingRawItem?.item_id || String(kpiMetric.title),
            type: 'sparc'
          });
        });
    }

    const filteredLighthouseMetrics: React.SetStateAction<any[]> = [];
    if (lighthouseCardsData && lighthouseCardsData.length > 0) {
      // Get favorited lighthouse metric item_ids
      const favoritedMetricIds = rawLighthouseMetrics.map((item: any) => String(item.item_name));
      // Filter lighthouseCardsData based on favorited item_ids matching card id
      const filteredCards = lighthouseCardsData
        .filter((card: any) => favoritedMetricIds.includes(String(card.title)))
        .forEach((card: any) => {
          // Find the corresponding rawLighthouseMetric to get isFavorite status and other metadata
          const matchingRawItem = rawLighthouseMetrics.find((item: any) => item.item_name === card.title);

          filteredLighthouseMetrics.push({
            ...card,
            moduleName: card.title,
            description: card.title,
            isFavorite: matchingRawItem?.is_favourite || false,
            item_id: matchingRawItem?.item_id || card.id,
            type: 'lighthouse'
          });
        });
    }

    setKfaFavorites(filteredKfas);
    setKpiFavorites(filteredKpis);
    setLighthouseMetricFavorites(filteredLighthouseMetrics);
  }, [favoritesFromRedux, lighthouseCardsData, kpiMetricsData]);

  // Sync KPI favorites from Redux to favoriteSparcCards for Continue button state
  useEffect(() => {
    if (kpiFavorites && kpiFavorites.length > 0) {
      // Extract titles/names from kpiFavorites to update favoriteSparcCards
      // Use title (the KPI name) instead of item_id since the same KPI has different IDs across daily/weekly/period
      const kpiNames = kpiFavorites.map((kpi: any) => kpi.title || kpi.description);
      console.log('🔄 Syncing favoriteSparcCards from kpiFavorites:', kpiNames);
      setFavoriteSparcCards(kpiNames);
    } else {
      // Clear favoriteSparcCards if no KPI favorites
      console.log('🔄 Clearing favoriteSparcCards (no kpiFavorites)');
      setFavoriteSparcCards([]);
    }
  }, [kpiFavorites]);

  // Sync Lighthouse Metric favorites from Redux to favoriteLighthouseCards for Continue button state
  useEffect(() => {
    if (lighthouseMetricFavorites && lighthouseMetricFavorites.length > 0) {
      // Extract IDs or titles from lighthouseMetricFavorites to update favoriteLighthouseCards
      const lighthouseIds = lighthouseMetricFavorites.map((metric: any) => metric.id || metric.title || metric.moduleName || metric.description);
      setFavoriteLighthouseCards(lighthouseIds);
    } else {
      // Clear favoriteLighthouseCards if no Lighthouse Metric favorites
      setFavoriteLighthouseCards([]);
    }
  }, [lighthouseMetricFavorites]);

  // NOTE: allKpiCards state management removed - now using Redux (reviewProgress.selectedKpi)
  // Selected KPIs are computed and stored in Redux by SparcKpiListSection
  // and fetched directly from Redux in ReviewProgressTab

  // Sync KFA favorites from Redux to local favoriteLighthouseTasks state for pre-population
  useEffect(() => {
    if (kfaFavorites && kfaFavorites.length > 0) {
      // Get all lighthouse tasks from the hook
      const allLighthouseTasks = lighthouseTasksHook?.tasks || [];
      const flatTasks = allLighthouseTasks.flatMap((category: any) => category.tasks || []);

      // Match KFA favorites with task IDs
      const matchedTaskIds: string[] = [];

      kfaFavorites.forEach((favorite: any) => {
        // Match by kfa_id (item_id in favorites corresponds to kfa_id in tasks)
        const matchingTask = flatTasks.find((task: any) => {
          const taskKfaId = task.kfa_id?.toString();
          const favoriteItemId = favorite.item_id?.toString();
          return taskKfaId === favoriteItemId;
        });

        if (matchingTask) {
          matchedTaskIds.push(matchingTask.id);
        }
      });

      // Update favoriteLighthouseTasks state with matched IDs
      if (matchedTaskIds.length > 0) {
        setFavoriteLighthouseTasks(prev => {
          // Merge with existing to avoid duplicates
          const combined = [...new Set([...prev, ...matchedTaskIds])];
          return combined;
        });
      }
    }
  }, [kfaFavorites, lighthouseTasksHook?.tasks]);

  // Use custom hook for all event handlers
  const {
    handleOpenNewsFeedOverlay,
    handleCloseNewsFeedOverlay,
    handleSaveNews,
    handleLighthouseTaskContinue,
    handleLighthouseCardActionPlan,
    handleSparcCardActionPlan,
    handleActionPlanBack,
    handleDeleteNews,
    deletingNewsId,
    handleBackToEditPress,
    backtoDashboardPress,
    areButtonsDisabledDueToLoading,
    handleBack,
    handleSaveForLater,
    handleCancelSaveForLater,
    handleConfirmSaveForLater,
    handleContinue,
    handleSubmit,
    handleStandupCompletion,
    handleActionPlanSubmit,
    toggleFavoriteCard,
    handleToggleFavorite,
    handleToggleLighthouseFavorite,
    handleToggleFavoriteLighthouseTask,
    handleViewOpportunityDetails,
    handleAddNews,
    isAddingNewsLoading,
    handleBackFromDetails,
    handleActionPlanSaved,
    handleViewActionPlanDetails,
    handleBackFromActionPlanDetails,
    handleKeyActivitiesRefresh,
    handleToggleFavoriteOpportunityWithSync,
    handleAllKpiCardsChange,
    handleBreadcrumbBack,
    handleTopOpportunityToggleWithModal,
    handleConfirmUnfavouriteOpportunity,
    handleCancelUnfavouriteOpportunity,
  } = useStandUpRedirectHandlers({
    // State setters
    setNewsFeedOverlayVisible,
    setSessionNewsList,
    setApiNewsList,
    setStandupNewsList,
    setGlobalNewsList,
    setSelectedLighthouseTask,
    setShowActionPlanScreen,
    setSelectedLighthouseCard,
    setActiveTab,
    setIsBackToEditInViewMode,
    setShowSaveForLaterModal,
    setCompletedLighthouseTaskIds,
    setAllLighthouseTasks,
    setCompletedStandupStatus,
    setFavoriteSparcCards,
    setFavoriteLighthouseCards,
    setFavoriteLighthouseTasks,
    setNewsTitle,
    setNewsDescription,
    setSelectedOpportunity,
    setSelectedActionPlanForView,
    setNewlyCreatedStandupId,
    newlyCreatedStandupId,
    setAllKpiCards,
    setKeyActivitiesRefreshFn,
    setNewsTitleError,
    setNewsDescriptionError,
    setShowTopOpportunityUnfavouriteModal,
    setPendingUnfavouriteOpportunityId,
    // UI state
    activeTab,
    pendingUnfavouriteOpportunityId,
    isViewMode,
    isEditMode,
    viewStandupData,
    newsTitle,
    newsDescription,
    selectedLighthouseTask,
    completedLighthouseTaskIds,
    allLighthouseTasks,
    keyActivitiesRefreshFn,
    apiNewsList,
    // Loading states
    favoritesLoading,
    lighthouseExecutionLoading,
    kpiMetricsLoading,
    lighthouseTasksLoading,
    newsLoading,
    // Data
    standupSessionId,
    standupId,
    standupDate,
    sessionData,
    periodWeekData,
    standupStatus,
    userEmail,
    selectedLocation,
    topOpportunitiesFromRedux,
    // Callbacks
    onBackPress,
    submitStandUp,
    updateStandup,
    fetchTopOpportunitiesData,
    fetchStandUpData,
    fetchNewsData,
    refreshStandupNews,
    handleToggleFavoriteOpportunity,
  });

  // Handler for toggling news favorite that's not in the hook
  const setOnToggleFavoriteForNews = (newsId: number) => {
    console.log('Toggling favorite for news ID:', newsId);
  }

  const handleToggleForNewsFavorite = async (newsId: number) => {
    try {
      setTogglingNewsId(newsId);
      const allNews = [...sessionNewsList, ...apiNewsList];
      const newsItem = allNews.find(item => parseInt(item.id) === newsId);

      if (!newsItem) {
        console.error("News item not found:", newsId);
        dispatch(
          setToastNotification({
            header: "Error",
            subtext: "News item not found",
            variant: "error",
            position: "top",
            duration: 3000,
          })
        );
        setTogglingNewsId(null);
        return;
      }

      const wasStandupSpecific = newsItem.standup_id || newsItem.session_id;
      const isUnfavoriting = newsItem.is_favourite === true;

      // Call the hook to toggle favorite
      const result = await toggleNewsFavorite(newsId, newsItem);

      if (result.success) {
        // Update local state immediately instead of waiting for backend refresh
        const updatedNewsItem = {
          ...newsItem,
          is_favourite: !newsItem.is_favourite,
          // If unfavoriting standup news, remove standup association
          standup_id: (wasStandupSpecific && isUnfavoriting) ? null : newsItem.standup_id,
          session_id: (wasStandupSpecific && isUnfavoriting) ? null : newsItem.session_id,
        };

        if (wasStandupSpecific && isUnfavoriting) {
          // Unstarring standup news: Remove from standupNewsList, add to globalNewsList
          setStandupNewsList(prev => prev.filter(n => n.id !== newsItem.id));
          setGlobalNewsList(prev => {
            // Check if already exists in global list
            const existsInGlobal = prev.some(n => n.id === newsItem.id);
            if (existsInGlobal) {
              return prev.map(n => n.id === newsItem.id ? updatedNewsItem : n);
            } else {
              return [updatedNewsItem, ...prev];
            }
          });
          setApiNewsList(prev => prev.map(n => n.id === newsItem.id ? updatedNewsItem : n));
        } else if (!wasStandupSpecific && !isUnfavoriting) {
          // Starring global news: Move from globalNewsList to standupNewsList (no duplicates)
          const updatedWithStandupLink = {
            ...updatedNewsItem,
            standup_id: standupId ? parseInt(standupId.toString()) : null,
            session_id: standupSessionId || null,
          };
          // Remove from global list
          setGlobalNewsList(prev => prev.filter(n => n.id !== newsItem.id));
          // Add to standup list (check if not already there)
          setStandupNewsList(prev => {
            const existsInStandup = prev.some(n => n.id === newsItem.id);
            if (existsInStandup) {
              return prev.map(n => n.id === newsItem.id ? updatedWithStandupLink : n);
            } else {
              return [updatedWithStandupLink, ...prev];
            }
          });
          setApiNewsList(prev => prev.map(n => n.id === newsItem.id ? updatedWithStandupLink : n));
        } else {
          // Just update the is_favourite flag in the current list
          if (wasStandupSpecific) {
            setStandupNewsList(prev => prev.map(n => n.id === newsItem.id ? updatedNewsItem : n));
          } else {
            setGlobalNewsList(prev => prev.map(n => n.id === newsItem.id ? updatedNewsItem : n));
          }
          setApiNewsList(prev => prev.map(n => n.id === newsItem.id ? updatedNewsItem : n));
        }
      }
    } catch (error: any) {
      console.error("Error toggling favorite for news:", error);
    } finally {
      setTogglingNewsId(null);
    }
  }

  // Avoid logging on every render; only log when value changes.
  useEffect(() => {
    logger.log('standupStatus:++++++', standupStatus);
  }, [standupStatus]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'review-performance':
        return (
          <ReviewPerformanceTab
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            onToggleSparcFavorite={handleToggleFavorite}
            onToggleLighthouseFavorite={handleToggleLighthouseFavorite}
            selectedPeriod={periodWeekData?.period || ''}
            lighthouseCardsData={lighthouseCardsData}
            standupType={standupType}
            standupId={standupId || undefined}
            selectedLocation={memoizedSelectedLocation}
            isLoadingLighthouse={isLoadingLighthouse}
            onAllCardsChange={handleAllKpiCardsChange}
            onLighthouseCardActionPlan={handleLighthouseCardActionPlan}
            onSparcCardActionPlan={handleSparcCardActionPlan}
            metricTrendData={metricTrendData}
          />
        );
      case 'key-activities':
        return (
          <KeyActivitiesTab
            favoriteLighthouseTasks={favoriteLighthouseTasks}
            onToggleFavoriteLighthouseTask={handleToggleFavoriteLighthouseTask}
            standupType={standupType}
            standupSessionId={standupSessionId}
            selectedLocation={memoizedSelectedLocation}
            onAllTasksChange={setAllLighthouseTasks}
            onContinueToActionPlan={handleLighthouseTaskContinue}
            onViewActionPlanDetails={handleViewActionPlanDetails}
            favoritesHook={favoritesHook}
            lighthouseTasksHook={lighthouseTasksHook}
            completedLighthouseTaskIds={completedLighthouseTaskIds}
            onRefreshTopOpportunities={() => fetchTopOpportunitiesData(1, 10, standupSessionId, "no_need")}
            onRefreshKeyActivitiesData={handleKeyActivitiesRefresh}
          />
        );
      case 'news':
        return (
          <NewsTab
            newsTitle={newsTitle}
            newsDescription={newsDescription}
            standupNewsList={standupNewsList}
            allNewsList={[...standupNewsList, ...globalNewsList]}
            currentNewsPage={currentNewsPage}
            onNewsPageChange={setCurrentNewsPage}
            onTitleChange={(text) => {
              setNewsTitle(text);
              if (newsTitleError) setNewsTitleError(false);
            }}
            onDescriptionChange={(text) => {
              setNewsDescription(text);
              if (newsDescriptionError) setNewsDescriptionError(false);
            }}
            onAddNews={handleAddNews}
            onDeleteNews={handleDeleteNews}
            deletingNewsId={deletingNewsId}
            onToggleFavorite={handleToggleForNewsFavorite}
            togglingNewsId={togglingNewsId}
            onViewMore={(newsId) => {
              setSelectedNewsId(newsId);
              setNewsDetailsOverlayVisible(true);
            }}
            titleError={newsTitleError}
            descriptionError={newsDescriptionError}
            onTitleFocus={() => {
              if (newsTitleError) setNewsTitleError(false);
            }}
            onDescriptionFocus={() => {
              if (newsDescriptionError) setNewsDescriptionError(false);
            }}
            isAddingNews={isAddingNewsLoading}
          />
        );
      case 'review-progress':
        return (
          <ReviewProgressTab
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            favoriteLighthouseTasks={favoriteLighthouseTasks}
            allTasks={allLighthouseTasks}
            allKpiCards={allKpiCards}
            standupNewsList={standupNewsList}
            currentNewsPage={currentReviewNewsPage}
            onNewsPageChange={setCurrentReviewNewsPage}
            onDeleteNews={handleDeleteNews}
            onOpenNewsFeedOverlay={handleOpenNewsFeedOverlay}
            onViewMore={(newsId) => {
              setSelectedNewsId(newsId);
              setNewsDetailsOverlayVisible(true);
            }}
            onToggleFavorite={handleToggleForNewsFavorite}
            togglingNewsId={togglingNewsId}
            isViewMode={isViewMode}
            kfaFavorites={kfaFavorites}
            kpiFavorites={kpiFavorites}
            lighthouseMetricFavorites={lighthouseMetricFavorites}
          />
        );
      default:
        return (
          <ReviewPerformanceTab
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            onToggleSparcFavorite={handleToggleFavorite}
            onToggleLighthouseFavorite={handleToggleLighthouseFavorite}
            selectedPeriod={periodWeekData?.period || ''}
            lighthouseCardsData={lighthouseCardsData}
            standupType={standupType}
            standupId={standupId || undefined}
            selectedLocation={memoizedSelectedLocation}
            onLighthouseCardActionPlan={handleLighthouseCardActionPlan}
            onSparcCardActionPlan={handleSparcCardActionPlan}
            metricTrendData={metricTrendData}
          />
        );
    }
  };

  // Helper functions for responsive design
  const isMobileView = () => {
    const windowWidth = Dimensions.get('window').width;
    return Platform.OS !== 'web' || windowWidth < 768;
  };

  const isIPadView = () => {
    return Platform.OS === 'ios' && Dimensions.get('window').width >= 768;
  };

  // Breadcrumb items
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: "stand-up",
      label: "Stand Up",
      onPress: handleBreadcrumbBack,
    },
    {
      id: "new-stand-up",
      label: isViewMode ? "View Stand Up" : (isEditMode ? "Edit Stand Up" : "New Stand Up"),
      isCurrentPage: true,
    },
  ];

  const showBreadcrumb = Platform.OS === "web" || isIPadView();
  const showMobileBack = isMobileView() && !isIPadView();

  // Generate unique key for action plan screen to force remount when switching between sections
  // This prevents stale form data from persisting across different card types
  const actionPlanScreenKey = React.useMemo(() => {
    if (selectedLighthouseTask) {
      // Lighthouse task from KeyActivitiesTab
      const taskType = selectedLighthouseTask.isContinuousImprovement ? 'ci' : 'task';
      return `${taskType}-${selectedLighthouseTask.id || selectedLighthouseTask.kfa_id || Date.now()}`;
    }
    if (selectedLighthouseCard) {
      // Card from ReviewPerformanceTab (SPARC KPI or Lighthouse Execution)
      if (selectedLighthouseCard.isSparcKpi) {
        return `sparc-${selectedLighthouseCard.id || selectedLighthouseCard.card_id || Date.now()}`;
      }
      if (selectedLighthouseCard.isLighthouseExecution) {
        return `lhe-${selectedLighthouseCard.id || selectedLighthouseCard.question_id || Date.now()}`;
      }
      return `card-${selectedLighthouseCard.id || Date.now()}`;
    }
    return 'default';
  }, [selectedLighthouseTask, selectedLighthouseCard]);

  return (
    <PlatformContainer style={styles.container}>
      {showActionPlanScreen && (selectedLighthouseTask || selectedLighthouseCard) ? (
        <StandUpCreateActionPlanScreen
          key={actionPlanScreenKey}
          cardData={selectedLighthouseTask || selectedLighthouseCard}
          standupSessionId={standupSessionId}
          standupId={standupId}
          isEditMode={isEditMode}
          onBack={handleActionPlanBack}
          onSubmit={handleActionPlanSubmit}
          isContinuousImprovement={selectedLighthouseTask?.isContinuousImprovement}
          isLighthouseExecution={selectedLighthouseCard?.isLighthouseExecution}
          isSparcKpi={selectedLighthouseCard?.isSparcKpi}
        />
      ) : selectedOpportunity ? (
        <TopOpportunityDetails
          onBackPress={handleBackFromDetails}
          actionPlanId={String(selectedOpportunity?.ID)}
          actionPlanData={selectedOpportunity}
          onActionPlanSaved={handleActionPlanSaved}
          backButtonText="Back to Standup"
          showTopOpportunityToggle={true}
          sessionId={standupSessionId}
          onActionPlanDeleted={(deletedId: string) => {
          const updatedTopOpportunities = topOpportunitiesFromRedux.filter(
              (item: TopOpportunityItem) => String(item.ID) !== String(deletedId)
            );
          dispatch(setTopOpportunitiesRedux(updatedTopOpportunities));
          }}
        />
      ) : selectedActionPlanForView ? (
        <TopOpportunityDetails
          onBackPress={handleBackFromActionPlanDetails}
          actionPlanId={String(selectedActionPlanForView?.ID)}
          actionPlanData={selectedActionPlanForView}
          onActionPlanSaved={handleActionPlanSaved}
          backButtonText="Back to Key Activities"
          showTopOpportunityToggle={false}
          sessionId={standupSessionId}
        />
      ) : (
        <>
          {/* Mobile Back Button */}
          {showMobileBack && (
            <View style={styles.mobileHeaderContainer}>
              <TouchableOpacity
                onPress={handleBreadcrumbBack}
                style={styles.mobileBackButton}
              >
                <MaterialIcons name="arrow-back" size={24} color="#005CBC" />
                <Text style={styles.mobileBackButtonText}>
                  Back to Stand Up
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {/* NewsFeed Overlay */}
          <NewsFeedOverlay
            visible={newsFeedOverlayVisible}
            onDismiss={handleCloseNewsFeedOverlay}
            onSave={handleSaveNews}
            userEmail={userEmail}
            locationId={
              selectedLocation?.id ? Number(selectedLocation.id) : null
            }
            locationName={selectedLocation?.displayText ?? ""}
          />

          {/* News Details Overlay */}
          <NewsDetailsOverlay
            visible={newsDetailsOverlayVisible}
            onDismiss={() => setNewsDetailsOverlayVisible(false)}
            newsId={selectedNewsId}
          />

          {/* Save for Later Confirmation Modal */}
          <LHModalPop
            visible={showSaveForLaterModal}
            onClose={handleCancelSaveForLater}
            title="Save for later"
            showFooter={true}
            primaryButtonText="Save"
            secondaryButtonText="Cancel"
            onPrimaryPress={handleConfirmSaveForLater}
            onSecondaryPress={handleCancelSaveForLater}
            showCloseButton={true}
          >
            <View style={{ gap: 12, paddingVertical: 8 }}>
              <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
                Are you sure you want to save your progress and exit?
              </Text>
              <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
                You can continue where you left off from the stand up dashboard.
              </Text>
            </View>
          </LHModalPop>

          {/* Top Opportunities Unfavourite Confirmation Modal */}
          <LHModalPop
            visible={showTopOpportunityUnfavouriteModal}
            onClose={handleCancelUnfavouriteOpportunity}
            title={getUnfavouriteModalConfig('top-opportunities').header}
            showFooter={true}
            primaryButtonText={getUnfavouriteModalConfig('top-opportunities').primaryButton}
            secondaryButtonText={getUnfavouriteModalConfig('top-opportunities').secondaryButton}
            onPrimaryPress={handleConfirmUnfavouriteOpportunity}
            onSecondaryPress={handleCancelUnfavouriteOpportunity}
            showCloseButton={true}
          >
            <View style={{ gap: 12, paddingVertical: 8 }}>
              <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
                {getUnfavouriteModalConfig('top-opportunities').bodyLine1}
              </Text>
              <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
                {getUnfavouriteModalConfig('top-opportunities').bodyLine2}
              </Text>
            </View>
          </LHModalPop>

          {/* Breadcrumb Navigation */}

          {/* Breadcrumb Navigation - Web/iPad only */}
          {showBreadcrumb && (
            <View style={styles.breadcrumbContainer}>
              <DLOBreadcrumb
                items={breadcrumbItems}
                showHomeIcon={true}
                onHomePress={onHomePress}
                style={{ flexDirection: "row", alignItems: "center" }}
              />
            </View>
          )}

          {/* Stepper Navigation */}
          <StepperNavigation
            active={activeTab}
            onChange={(tabId) => setActiveTab(tabId)}
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            favoriteLighthouseTasks={favoriteLighthouseTasks}
            selectedKpiCards={selectedKpiFromRedux || []}
            isBackToEditInViewMode={isBackToEditInViewMode}
            isViewMode={isViewMode}
            isSubmitted={isSubmitted}
          />

          {/* Header Section with Title and Buttons */}
          <HeaderSection
            onBackPress={handleBack}
            backtoDashboard={backtoDashboardPress}
            backToEditPress={handleBackToEditPress}
            onSavePress={handleSaveForLater}
            onContinuePress={handleContinue}
            onSubmitPress={handleSubmit}
            onCompletePress={handleStandupCompletion}
            activeTab={activeTab}
            standupType={standupType}
            standupDate={standupDate}
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            favoriteLighthouseTasks={favoriteLighthouseTasks}
            selectedKpiCards={selectedKpiFromRedux || []}
            isViewMode={isViewMode}
            isSubmitted={isSubmitted}
            standupItemStatus={completedStandupStatus}
            isBackToEditInViewMode={isBackToEditInViewMode}
            disableSaveAndContinue={areButtonsDisabledDueToLoading()}
          />

          {/* Top Opportunities Section */}
          <TopOpportunitiesSection
            isExpanded={isTopOpportunitiesExpanded}
            onToggle={() =>
              setIsTopOpportunitiesExpanded(!isTopOpportunitiesExpanded)
            }
            favoriteOpportunities={favoriteOpportunities}
            onToggleFavorite={handleTopOpportunityToggleWithModal}
            topOpportunitiesData={filteredAndSortedTopOpportunities}
            onViewDetailsPress={handleViewOpportunityDetails}
            isLoadingTopOpportunities={isLoadingTopOpportunitiesFromHook || isTogglingFavorite}
          />

          {/* Tab Content */}
          <View style={styles.tabContentContainer}>{renderTabContent()}</View>

          {/* Bottom Action Buttons */}
          <StandUpBottomActions
            activeTab={activeTab}
            onBackPress={handleBack}
            backToEditPress={handleBackToEditPress}
            onSubmitPress={handleSubmit}
            onSavePress={handleSaveForLater}
            onContinuePress={handleContinue}
            onCompletePress={handleStandupCompletion}
            isEditMode={isEditMode}
            isViewMode={isViewMode}
            isSubmitted={isSubmitted}
            isBackToEditInViewMode={isBackToEditInViewMode}
            favoriteSparcCards={favoriteSparcCards}
            favoriteLighthouseCards={favoriteLighthouseCards}
            favoriteLighthouseTasks={favoriteLighthouseTasks}
            standupItemStatus={completedStandupStatus}
            selectedKpiCards={selectedKpiFromRedux || []}
          />

          {/* Centralized Error Modal */}
          <StandUpErrorModal />
        </>
      )}{" "}
    </PlatformContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F9FF',
    paddingVertical: 20,
    paddingHorizontal: 24,
  },
  tabContentContainer: {
    // flex: 1,
    // backgroundColor: '#FFFFFF',
    // borderRadius: 12,
    // padding: 24,
    // marginTop: 8,
  },
  mobileHeaderContainer: {
    marginBottom: 16,
  },
  mobileBackButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  mobileBackButtonText: {
    fontSize: 16,
    color: "#005CBC",
    fontFamily: FontFamily.regular,
  },
  breadcrumbContainer: {
    marginBottom: 24,
    paddingHorizontal: Platform.OS === "web" ? 0 : 8,
  },
});

export default StandUpRedirectScreen;